import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '../../services/authService';
import toast from 'react-hot-toast';

// Helper to extract error message from FastAPI responses
const getErrorMessage = (err, fallback) => {
  const data = err.response?.data;
  if (!data) return fallback;
  // FastAPI returns {detail: "string"} or {detail: [{msg: "..."}]}
  if (typeof data.detail === 'string') return data.detail;
  if (Array.isArray(data.detail)) return data.detail.map((e) => e.msg).join(', ');
  if (data.message) return data.message;
  return fallback;
};

// Async thunks
export const loginUser = createAsyncThunk(
  'auth/login',
  async (phone, { rejectWithValue }) => {
    try {
      const { data } = await authService.login(phone);
      return data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Failed to send OTP'));
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async ({ phone, otp }, { rejectWithValue }) => {
    try {
      const { data } = await authService.verifyOtp(phone, otp);
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('refresh_token', data.refresh_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      return data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Invalid OTP'));
    }
  }
);

export const registerUser = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await authService.register(userData);
      return data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err, 'Registration failed'));
    }
  }
);

export const fetchProfile = createAsyncThunk(
  'auth/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await authService.getProfile();
      localStorage.setItem('user', JSON.stringify(data));
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load profile');
    }
  }
);

// Re-read the signed-in user from the server so an admin role change
// (e.g. Host → Logistics Partner) shows up without logging out: the cached
// `user` in localStorage is otherwise only written at login. When the role
// differs, also refresh the access token so its roles/permissions claims
// match the new role (the backend rebuilds them from user_roles on refresh).
export const syncUserFromServer = createAsyncThunk(
  'auth/syncUserFromServer',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { data } = await authService.getProfile();
      const cachedRole = String(getState().auth.user?.role || '').toUpperCase();
      const freshRole = String(data?.role || '').toUpperCase();
      const refreshToken = localStorage.getItem('refresh_token');
      if (cachedRole && freshRole && cachedRole !== freshRole && refreshToken) {
        try {
          const { data: tokens } = await authService.refreshToken(refreshToken);
          localStorage.setItem('access_token', tokens.access_token);
          if (tokens.refresh_token) localStorage.setItem('refresh_token', tokens.refresh_token);
        } catch {
          // Keep the old token; the next 401 → refresh path picks up the
          // new claims. The menu still switches to the new role below.
        }
      }
      // Merge, don't replace — keep any login-time fields /users/me lacks.
      const merged = { ...(getState().auth.user || {}), ...data };
      localStorage.setItem('user', JSON.stringify(merged));
      return merged;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to sync profile');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const { data } = await authService.updateProfile(profileData);
      localStorage.setItem('user', JSON.stringify(data));
      toast.success('Profile updated successfully');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update profile');
    }
  }
);

// Initial state
const storedUser = localStorage.getItem('user');
const initialState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  isAuthenticated: !!localStorage.getItem('access_token'),
  loading: false,
  otpSent: false,
  otpPhone: null,
  error: null,
  profileLoading: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.otpSent = false;
      state.otpPhone = null;
      state.error = null;
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    },
    clearError: (state) => {
      state.error = null;
    },
    resetOtp: (state) => {
      state.otpSent = false;
      state.otpPhone = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.otpSent = true;
        state.otpPhone = action.meta.arg;
        toast.success('OTP sent to your phone');
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Verify OTP
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.otpSent = false;
        state.otpPhone = null;
        toast.success('Login successful');
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Register
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.loading = false;
        toast.success('Registration successful! Please verify your phone.');
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Profile
      .addCase(fetchProfile.pending, (state) => {
        state.profileLoading = true;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.user = action.payload;
      })
      .addCase(fetchProfile.rejected, (state) => {
        state.profileLoading = false;
      })
      .addCase(syncUserFromServer.fulfilled, (state, action) => {
        if (action.payload) state.user = action.payload;
      })
      // Update Profile
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.user = action.payload;
      });
  },
});

export const { logout, clearError, resetOtp } = authSlice.actions;
export default authSlice.reducer;
